import { useCallback, useState } from "react";

/**
 * Like useState, but the value is kept for this browser tab only (sessionStorage), so a person who moves
 * between files or tools does not have to set the same options again. It is cleared when the tab is closed,
 * and holds settings only, never file contents or text the person typed as content.
 */
export function useSessionState<T>(key: string, initial: T): [T, (v: T) => void] {
  const storageKey = `fdp-session-${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw !== null) return JSON.parse(raw) as T;
    } catch { /* storage blocked or corrupt: use the default */ }
    return initial;
  });
  const set = useCallback((v: T) => {
    setValue(v);
    try { sessionStorage.setItem(storageKey, JSON.stringify(v)); } catch { /* ignore */ }
  }, [storageKey]);
  return [value, set];
}
